import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, ShieldAlert, Trash2, CheckCircle2 } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { api, errMsg, dt } from "@/lib/api";
import { Panel, PageTitle, Badge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function KeyField({ k, pw, reload }) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = k.name.toLowerCase().replace(/_/g, "-");
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/keys/${k.name}`, { value, password: pw });
      setValue("");
      setShow(false);
      const v = r.data.verification;
      if (v?.ok === false) toast.error(`Saved, but verification failed: ${v.message}`);
      else toast.success(v?.message ? `Saved · ${v.message}` : "Saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${k.label}?`)) return;
    try {
      await api.delete(`/keys/${k.name}`, { data: { password: pw } });
      toast.success("Key removed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid={`key-field-${id}`} className="py-4 border-b border-zinc-800/70 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-200 font-medium">{k.label}</span>
          {!k.required && <span className="font-mono text-[10px] text-zinc-500">(optional)</span>}
        </div>
        <div className="flex items-center gap-2" data-testid={`key-status-${id}`}>
          {k.configured ? <Badge v="ok"><CheckCircle2 className="w-3 h-3" /> saved{k.source === "server" ? " (server)" : ""}</Badge> : <Badge v="data_unavailable">not set</Badge>}
          {k.updated_at && <span className="font-mono text-[10px] text-zinc-500">{dt(k.updated_at)}</span>}
        </div>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input data-testid={`key-input-${id}`} type={show ? "text" : "password"} autoComplete="off" spellCheck={false} value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={k.configured ? "•••••••••••• (enter a new value to replace)" : "Paste key"} className="pr-10 font-mono text-xs bg-[#090A0F]" />
          <button type="button" data-testid={`key-toggle-${id}`} onClick={() => setShow(!show)} aria-label={show ? "Hide" : "Show"} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <Button data-testid={`key-save-${id}`} onClick={save} disabled={busy || !value || !pw} className="bg-[#F7931A] hover:bg-[#E08213] text-black">{busy ? "Saving…" : "Save"}</Button>
        {k.source === "app" && <Button data-testid={`key-delete-${id}`} variant="ghost" size="icon" onClick={remove} disabled={!pw} aria-label="Remove key"><Trash2 className="w-4 h-4 text-zinc-500" /></Button>}
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const { data, reload } = usePoll("/keys", 0);
  const [pw, setPw] = useState("");
  return (
    <div>
      <PageTitle title="API Keys" sub="Keys are encrypted on the server and never sent back to the browser. After saving, only their status is shown." />
      <div data-testid="api-keys-warning" className="flex gap-3 border border-red-800 bg-red-950/30 rounded-lg p-4 mb-6">
        <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div className="text-sm text-red-200/90 space-y-1">
          <p className="font-semibold text-red-300">Warning: these keys give access to real funds.</p>
          <p>Anyone holding them can trade on your Kraken account. Only enable <b>query funds, query orders/trades, create & modify orders, cancel orders</b>. <b>Never enable withdrawal permission</b> on keys used by the bot. Use a key restricted to this app and revoke it on Kraken if you suspect misuse.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <Panel title="Exchange & data keys" icon={KeyRound} testid="api-keys-form">
          {(data || []).map((k) => <KeyField key={k.name} k={k} pw={pw} reload={reload} />)}
        </Panel>
        <Panel title="Confirm it's you">
          <p className="text-xs text-zinc-500 mb-3">Your owner password is required to save or remove any key.</p>
          <Input data-testid="api-keys-password-input" type="password" placeholder="Owner password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <div className="text-[11px] text-zinc-500 mt-4 space-y-1.5">
            <p>Kraken Spot keys: kraken.com → Settings → API.</p>
            <p>Kraken Futures keys: futures.kraken.com → Settings → API Keys (needed for SHORT trades).</p>
            <p>After saving Kraken keys the connection is verified automatically.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
